/**
 * Icelandic catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Icelandic at a mathematician's register. **Warrant → «Réttlæting»**
 * (epistemic justification — flagged; Icelandic has no settled Toulmin term). Refutation →
 * «hrakning» / «Hrakið». W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are one / other (Icelandic `one` is numbers ending in 1 except 11), so
 * `frameActive` declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const IS_CATALOG = {
    locale: 'is',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Rannsókn',
        [P2.sectionClaims]: 'Fullyrðingar',
        [P2.sectionIntegrity]: 'Heilleiki',
        [P2.axisWarrantLabel]: 'Réttlæting',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Reynslubundið',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Skilgreining} interp {Túlkun} testable {Prófanlegt} tested {Prófað} refuted {Hrakið} program {Rannsóknaráætlun} philos {Heimspekilegt} other {Óþekkt}}',
        [P2.axisModalLabel]: 'Háttbundið',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Styrkleikastig {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Kannanlegt',
        [P2.stateRefuted]: 'Hrakið',
        [P2.stateOutside]: 'Utan sviðs',
        [P2.verdictConflict]: 'Í árekstri við {constraints}',
        [P2.verdictOutside]: 'Krefst {missing}',
        [P2.lossRegisterPlain]: 'Einfaldað í almennt mál — hluti nákvæmninnar fellur niður.',
        [P2.lossRegisterKid]: 'Einfaldað fyrir yngri lesendur — mörgum smáatriðum er sleppt.',
        [P2.lossProfileDetail]: 'Heildarsniðið Réttlæting / Reynslubundið / Háttbundið er dregið saman hér.',
        [P2.lossConflictDetail]: 'Árekstursupplýsingarnar eru styttar í þessari sýn.',
        [P2.laneVerified]: 'Staðfest',
        [P2.laneUnverified]: 'Óstaðfest',
        [P2.laneConflict]: 'Árekstur sannaður',
        [P2.constraintLogic]: 'Rökfræði',
        [P2.constraintLightBends]: 'Ljós sveigist í þyngdarsviði',
        [P2.constraintParallelPostulate]: 'Samsíðufrumsendan',
        [P2.claimScalarGravity]: 'Skalar þyngdarkenning',
        [P2.claimNonEuclidean]: 'Óevklíðsk rúmfræði',
        [P2.claimSubstrateSr]: 'Undirlagstúlkun á takmörkuðu afstæðiskenningunni',
        [P2.edgeExtObservation]: 'Ytri athugun',
        [P2.edgeResourceSample]: 'Sameiginleg reikniauðlind',
        [P2.edgeHeuristicSample]: 'Heuristísk leiðsögn',
        [P2.edgeSharedSample]: 'Sameiginlegur grunnur',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Takmarkanir',
        [P2.sectionEdges]: 'Leggir',
        [P2.sectionFrame]: 'Rammi',
        [P2.sectionExposure]: 'Útsetning',
        [P2.statusAdoptedFormal]: 'Samþykkt (formlegt)',
        [P2.statusAdoptedInterpretive]: 'Samþykkt (túlkandi)',
        [P2.statusSuspended]: 'Frestað',
        [P2.edgeEffectConstraint]: 'Myndar takmörkun (getur hrakið)',
        [P2.edgeEffectResource]: 'Auðlind — engin áhrif á úrskurði',
        [P2.edgeEffectHeuristic]: 'Heuristík — engin áhrif á úrskurði',
        [P2.edgeEffectShared]: 'Sameiginlegur grunnur — engin áhrif á úrskurði',
        [P2.frameActive]: '{count, plural, =0 {Engar virkar takmarkanir} one {# virk takmörkun} other {# virkar takmarkanir}}',
        [P2.exposureNone]: 'Engin túlkandi vensl',
        [P2.exposureDep]: 'Veltur á {id}',
        [P2.laneTrackMachine]: 'Vél',
        [P2.laneTrackExpert]: 'Sérfræðingur',
        [P2.laneTrackCommunity]: 'Samfélag',
        [P2.expertAudited]: 'Endurskoðað',
        [P2.expertInProgress]: 'Í vinnslu',
        [P2.expertNotReviewed]: 'Ekki yfirfarið',
        [P2.communityFlags]: 'Merkingar til staðar',
        [P2.communityNone]: 'Engar merkingar',
        [P2.controlChallenge]: 'Mótmæla',
        [P2.controlActivate]: 'Virkja',
        [P2.controlMakeInterpretive]: 'Gera túlkandi',
        [P2.controlImportEdge]: 'Flytja inn legg',
        [P2.controlPromote]: 'Hækka',
        [P2.controlUndo]: 'Afturkalla',
        [P2.controlRedo]: 'Endurtaka',
        [P2.controlRegister]: 'Skrá',
        [P2.promoteRejected]: 'Hækkun hafnað',
        [P2.promoteAccepted]: 'Hækkun samþykkt',
        [P2.promoteReasonRefutationTerminal]: 'Sönnun getur ekki læknað hrakningu.',
        [P2.promoteReasonKeystone]: 'Lykilhækkun krefst reynslustuðnings.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Reynslugerðin er ekki fastsett.',
        [P2.promoteReasonModalNotFixed]: 'Háttarstigið er ekki fastsett.',
        [P2.promoteReasonWarrantNotImproved]: 'Réttlætingarstigið hefur ekki batnað.',
        [P2.evidenceComputation]: "{engine} reiknaði {op} = {result} undir {config}",
        [P2.lossNumericRounding]: "Niðurstaða námunduð að {dp} aukastöfum",
    },
};
//# sourceMappingURL=is.js.map